import React, { useState } from "react";
import {
  TrashIcon,
  ArchiveBoxIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";

const DeleteConfirmationModal = ({
  isOpen,
  onClose,
  onSoftDelete,
  onPermanentDelete,
  itemName,
  itemType = "record",
}) => {
  const [deleteReason, setDeleteReason] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleSoftDelete = async () => {
    setIsLoading(true);
    try {
      await onSoftDelete(deleteReason.trim() || null);
      onClose();
      setDeleteReason("");
    } catch (error) {
      console.error("Error in soft delete:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePermanentDelete = async () => {
    if (
      window.confirm(
        `Are you sure you want to PERMANENTLY delete this ${itemType}? This action cannot be undone.`
      )
    ) {
      setIsLoading(true);
      try {
        await onPermanentDelete();
        onClose();
        setDeleteReason("");
      } catch (error) {
        console.error("Error in permanent delete:", error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <ExclamationTriangleIcon className="h-6 w-6 text-orange-500" />
            <h2 className="text-xl font-bold text-gray-900">
              Delete {itemType}
            </h2>
          </div>
        </div>

        <div className="p-6">
          <p className="text-gray-600 mb-4">
            Choose how you want to delete <strong>{itemName}</strong>:
          </p>

          <div className="space-y-4">
            {/* Soft Delete Option */}
            <div className="border border-blue-200 rounded-lg p-4 bg-blue-50">
              <div className="flex items-start gap-3">
                <ArchiveBoxIcon className="h-5 w-5 text-blue-600 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-medium text-blue-900 mb-1">
                    Move to Deleted Records
                  </h4>
                  <p className="text-sm text-blue-700 mb-3">
                    Record will be moved to deleted records section and can be
                    restored later.
                  </p>

                  <div className="mb-3">
                    <label
                      htmlFor="deleteReason"
                      className="block text-sm font-medium text-blue-800 mb-1"
                    >
                      Reason for deletion (optional)
                    </label>
                    <textarea
                      id="deleteReason"
                      value={deleteReason}
                      onChange={(e) => setDeleteReason(e.target.value)}
                      className="w-full px-3 py-2 border border-blue-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      rows="2"
                      placeholder="Enter reason for deletion..."
                    />
                  </div>

                  <button
                    onClick={handleSoftDelete}
                    disabled={isLoading}
                    className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                  >
                    {isLoading
                      ? "Moving to Deleted Records..."
                      : "Move to Deleted Records"}
                  </button>
                </div>
              </div>
            </div>

            {/* Permanent Delete Option */}
            <div className="border border-red-200 rounded-lg p-4 bg-red-50">
              <div className="flex items-start gap-3">
                <TrashIcon className="h-5 w-5 text-red-600 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-medium text-red-900 mb-1">
                    Delete Permanently
                  </h4>
                  <p className="text-sm text-red-700 mb-3">
                    ⚠️ This action cannot be undone. The record will be
                    permanently removed from the system.
                  </p>

                  <button
                    onClick={handlePermanentDelete}
                    disabled={isLoading}
                    className="w-full bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                  >
                    {isLoading
                      ? "Permanently Deleting..."
                      : "Delete Permanently"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
          <button
            onClick={() => {
              onClose();
              setDeleteReason("");
            }}
            disabled={isLoading}
            className="w-full px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeleteConfirmationModal;
